import { inject } from '@angular/core';
import { HttpHeaders, HttpInterceptorFn } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, of, switchMap, take } from 'rxjs';

import { AuthenticationService } from './authentication.service';
import { CacheService } from '@v2/core/services/cache.service';
import { BrainnetPrincipal } from '@v2/core/security/models/brainnetprincipal';

export const authHeadersInterceptorFn: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthenticationService);
  const translate = inject(TranslateService);
  const cache = inject(CacheService);

  if (req.url.includes('/sign-in/')) {
    return next(req);
  }

  const language = translate.getCurrentLang() ?? 'en';
  const opId = cache.getOperationId();

  let headers = req.headers;
  headers = addHeaderValue(headers, 'X-Operation-Id', opId);
  headers = addHeaderValue(headers, 'x-language-locale', language);

  return principalFor(auth, req.url).pipe(
    switchMap((principal) => {
      if (principal?.activeMember?.identifier != null) {
        headers = addHeaderValue(headers, 'x-active-member', String(principal.activeMember.identifier));
      }

      return next(req.clone({ headers }));
    }),
  );
};

function principalFor(auth: AuthenticationService, url: string): Observable<BrainnetPrincipal | null> {
  const snapshot = auth.tryGetPrincipalSnapshot();
  if (snapshot || !auth.ensureActiveAccountSnapshot() || !waitsForPrincipal(url)) {
    return of(snapshot);
  }

  return auth.principal$.pipe(
    take(1),
    catchError(() => of(null)),
  );
}

function waitsForPrincipal(url: string): boolean {
  return !url.includes('/members/fetch-supplier-claims') && !url.includes('/assets/');
}

function addHeaderValue(httpHeaders: HttpHeaders, name: string, value: string): HttpHeaders {
  if (!httpHeaders.has(name)) {
    httpHeaders = httpHeaders.append(name, value);
  } else if (httpHeaders.get(name) !== value) {
    httpHeaders = httpHeaders.set(name, value);
  }
  return httpHeaders;
}
